import Vue from 'vue';
import { curry } from 'lodash';
import { contexts } from './registry-contexts';


/**
 * Deviate a useful name from the module's path
 * @param {string} path - The module path
 * @param {Regex} nameRegex - A regex splitting up the name protion of the path
 * @param {function} convertName - A name post processor
 * @return {string} The resulting module name
 */
export function getModuleName(path, nameRegex, convertName) {
    const matchedName = path.match(nameRegex);
    const name = matchedName[matchedName[1] === 'index' ? 0 : 1];
    return convertName ? convertName(name) : name;
}

/**
 * Fetch a module's content
 * @param {string} path - The module path
 * @param {object} context - A required context containing a number of modules
 * @return {*} The default value of the module
 */
export function getModule(path, context) {
    const value = context(path);
    return value.__esModule ? value.default : value;
}

/**
 * Iterate through all modules in a context and execute the given register method
 * @param {object} config - The module config object
 * @return {*} The result of the last module's register method
 */
export function loadModules({ loadContext, register, ...rest }) {
    const context = loadContext();
    return context.keys().reduce((acc, path) => register(acc, { path, context, ...rest }), {});
}

/**
 * Load a single context via its context config and a given runtime config
 * @param {object} runtimeConfig - A runtime config object
 * @param {object} contextConfig - A context config object
 */
export function registerVueContext(runtimeConfig, contextConfig) {
    loadModules({ ...contextConfig, ...runtimeConfig });
}

/**
 * Register an asset to the Vue framework
 * @param {object} context - A required context containing a number of modules
 * @param {string} path - The module path
 * @param {function} convertName - A name conversion function
 * @param {string} namespace - An optional namespace to prefix the module name
 * @param {string} type - The type of asset that is being added
 */
export function registerVueAsset({ context, path }, { namespace, convertName, type }) {
    const name = getModuleName(path, /\/([^/]+)\/([a-zA-Z0-9-]+)\.(?:js|vue$)/, convertName);
    const value = getModule(path, context);
    const key = namespace ? `${ namespace }-${ name }` : name;

    Vue[type](key, value);
}

/**
 * Register all modules
 * @param {object} runtimeConfig - Dynamic config that has been initialized directly before the registry
 * @return {Promise} Resolve when all contexts have been loaded
 */
export function init(runtimeConfig) {
    const loadContextWithRuntimeConfig = curry(registerVueContext)(runtimeConfig);
    return Promise.all(contexts).then(contextConfigs => contextConfigs.forEach(loadContextWithRuntimeConfig));
}

export default init;
